import { useLayoutEffect, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import type { EditorState, TransactionSpec } from '@codemirror/state';
import { useApp } from '../app/context';
import { patchUi, ui } from '../app/ui';
import { runSpec } from '../editor/setup';
import { applyLink, linkAt } from '../editor/markdownCommands';
import { CheckIcon, CloseIcon } from './icons';
import { iconGesture, type IconGesture } from './iconMotion';

/** Shared mechanics of the two pills: commands, the hover lamp, roving focus, width morphing. */

export function useCommand() {
  const app = useApp();
  return (make: (state: EditorState) => TransactionSpec | null) => {
    const view = app.view();
    if (!view) return;
    const keepFocus = view.hasFocus;
    runSpec(view, make);
    patchUi({ typing: false });
    if (keepFocus) view.focus();
  };
}

/**
 * A single soft highlight that slides between the controls under the
 * pointer, so the pill reads as one object rather than a row of buttons.
 */
export function useLamp(container: RefObject<HTMLElement | null>, lamp: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const root = container.current;
    const el = lamp.current;
    if (!root || !el) return;
    let lit = false;
    const over = (e: PointerEvent) => {
      const target = (e.target as Element).closest<HTMLElement>('[data-lamp]');
      if (!target || !root.contains(target) || (target as HTMLButtonElement).disabled) {
        el.dataset.on = 'false';
        lit = false;
        return;
      }
      const r = target.getBoundingClientRect();
      const base = root.getBoundingClientRect();
      if (!lit) el.dataset.snap = 'true';
      el.style.transform = `translate(${r.left - base.left}px, ${r.top - base.top}px)`;
      el.style.width = `${r.width}px`;
      el.style.height = `${r.height}px`;
      el.dataset.on = 'true';
      if (!lit) requestAnimationFrame(() => delete el.dataset.snap);
      lit = true;
    };
    const leave = () => {
      el.dataset.on = 'false';
      lit = false;
    };
    root.addEventListener('pointerover', over);
    root.addEventListener('pointerleave', leave);
    return () => {
      root.removeEventListener('pointerover', over);
      root.removeEventListener('pointerleave', leave);
    };
  });
}

/** Arrow keys move between a toolbar's controls; Tab leaves the toolbar. */
export function toolbarKeys(e: KeyboardEvent<HTMLElement>) {
  if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
  if ((e.target as HTMLElement).tagName === 'INPUT') return;
  const items = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[data-tool]:not([disabled])'));
  const index = items.indexOf(document.activeElement as HTMLElement);
  const next = e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1 : (index + (e.key === 'ArrowRight' ? 1 : -1) + items.length) % items.length;
  e.preventDefault();
  items.forEach((item, i) => (item.tabIndex = i === next ? 0 : -1));
  items[next]?.focus();
}

/** Ensure exactly one control in a toolbar is reachable by Tab. */
export function useRovingStart(container: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const items = Array.from(container.current?.querySelectorAll<HTMLElement>('[data-tool]:not([disabled])') ?? []);
    if (items.length && !items.some((i) => i.tabIndex === 0)) items[0].tabIndex = 0;
  });
}

/** Animate an element's width to follow its content (the first measurement is instant). */
export function useMorphWidth(outer: RefObject<HTMLElement | null>, inner: RefObject<HTMLElement | null>, mounted: boolean) {
  useLayoutEffect(() => {
    const box = outer.current;
    const content = inner.current;
    if (!mounted || !box || !content) return;
    const apply = () => {
      // The box is border-box: its width must hold the content and its own padding.
      const style = getComputedStyle(box);
      const pad = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
      // Computed width is the layout width, unaffected by an entrance animation's scale.
      box.style.setProperty('width', `${Math.ceil(parseFloat(getComputedStyle(content).width) + pad)}px`);
      if (!box.hasAttribute('data-ready')) requestAnimationFrame(() => box.setAttribute('data-ready', 'true'));
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(content);
    return () => ro.disconnect();
  }, [outer, inner, mounted]);
}

interface ToolProps {
  label: string;
  kbd?: string;
  pressed?: boolean;
  onPress: () => void;
  children: ReactNode;
  className?: string;
  tipSide?: 'top' | 'bottom';
  haspopup?: boolean;
  expanded?: boolean;
  buttonRef?: (el: HTMLButtonElement | null) => void;
  /** How the icon answers a press. */
  gesture?: IconGesture;
}

export function Tool({ label, kbd, pressed, onPress, children, className = '', tipSide = 'top', haspopup, expanded, buttonRef, gesture }: ToolProps) {
  return (
    <button
      ref={buttonRef}
      type="button"
      className={`tool ${className}`}
      data-tool
      data-lamp
      tabIndex={-1}
      aria-label={label}
      aria-pressed={haspopup ? undefined : pressed}
      aria-haspopup={haspopup ? 'menu' : undefined}
      aria-expanded={haspopup ? expanded : undefined}
      data-tip={label}
      data-kbd={kbd}
      data-tip-side={tipSide}
      // Never take focus from the text on pointer press; the selection must survive.
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => {
        if (gesture) iconGesture(e.currentTarget, gesture, !pressed);
        onPress();
      }}
    >
      {children}
    </button>
  );
}

/** The link field a pill turns into. */
export function LinkField({ compact = false }: { compact?: boolean }) {
  const app = useApp();
  const view = app.view();
  const [existing] = useState(() => (view ? linkAt(view.state) : null));
  const [value, setValue] = useState(existing?.url ?? '');

  const finish = (url: string | null) => {
    patchUi({ linkEditor: null });
    const v = app.view();
    if (!v) return;
    if (url !== null) runSpec(v, (s) => applyLink(s, url));
    v.focus();
    // The link is made: the chain closes on itself, once, in the controls that offered it.
    if (url) requestAnimationFrame(() => document.querySelectorAll('.win[data-focused] .tool[aria-label="Link"]').forEach((el) => iconGesture(el, 'link')));
  };

  return (
    <form
      className="link-field"
      data-compact={compact || undefined}
      onSubmit={(e) => {
        e.preventDefault();
        finish(value);
      }}
    >
      <input
        autoFocus
        type="text"
        inputMode="url"
        aria-label={existing ? 'Edit link address' : 'Link address'}
        placeholder="Paste or type a link"
        value={value}
        spellCheck={false}
        autoComplete="off"
        autoCapitalize="off"
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            finish(null);
          }
        }}
        onBlur={(e) => {
          if (!e.currentTarget.form?.contains(e.relatedTarget as Node) && ui.get().linkEditor) patchUi({ linkEditor: null });
        }}
      />
      {existing && (
        <button type="button" className="link-action" aria-label="Remove link" data-tip="Remove link" onMouseDown={(e) => e.preventDefault()} onClick={() => finish('')}>
          <CloseIcon size={14} />
        </button>
      )}
      <button type="submit" className="link-action link-apply" aria-label="Apply link" data-tip="Apply" data-kbd="↵" onMouseDown={(e) => e.preventDefault()} disabled={!value.trim() && !existing}>
        <CheckIcon size={15} />
      </button>
    </form>
  );
}
